/* site/pages/PartnerLoginPage.jsx — lhc-site/login.html (`section.account-layout`),
 * re-ported to the 2026-09-07 prototype redesign.
 *
 * THIS PAGE IS LIVE SOFTWARE, not a mock. The re-port is a re-skin only:
 *  · "Sign In" is the platform's REAL sign-in (AuthContext → POST /api/auth/login)
 *    with the real error handling, the brand boot splash and the real redirect to
 *    the account's default route (2026-09-07 user: "the login we have now → the
 *    footer Partner login; add the demo accounts there").
 *  · the demo cards are the seeded evaluation accounts; QA drives them by their
 *    data-testid="quick-login-<key>" attributes — never rename them.
 *  · "Apply for an Account" is the REAL partner application (POST /api/auth/signup,
 *    reviewed in the superadmin) and must confirm with the exact sentence
 *    "Thank you for applying. We will contact you soon."
 *  · /partners/login and /partners/apply both render this page; the second opens
 *    the application pane (the prototype's `login.html#apply` deep link).
 *
 * The prototype's own script is a front-end stub (it fakes a sign-in and fakes a
 * submission) — its DATA (account types, dealer-only fields, the tab/type
 * behaviour) is ported, its fake submissions are not. Its two "not connected —
 * for design review only" status notes are deliberately dropped because they are
 * untrue here; see content/partnerAccount.js. */
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import "./partner-login.css";
import { usePageMeta } from "../SiteLayout";
import { useSiteContent } from "../content/SiteContent";
import PARTNER, { DEMO_ACCOUNTS, demoKey } from "../content/partnerAccount";
import { SiteLink } from "../siteLinks";
import { onPlaceholderError } from "../placeholderImg";
import { useAuth } from "@/auth/AuthContext";
import { playBootSplash } from "@/components/BootSplash";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const homeFor = (u) => (u && u.default_route) || "/store";

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function PartnerLoginPage() {
  const c = useSiteContent("partnerAccount", PARTNER);
  usePageMeta(c.meta);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, status, user } = useAuth();
  /* /partners/apply is the route form of the prototype's `login.html#apply` deep link;
     an old `#apply` hash on the sign-in route opens the same pane, as the prototype did */
  const tab = /\/apply\/?$/.test(location.pathname) || location.hash === "#apply" ? "apply" : "signin";
  const showPane = (name) => navigate(name === "apply" ? "/partners/apply" : "/partners/login", { replace: true });
  const signingRef = useRef(false);

  /* already signed in → straight to the workspace (unless a sign-in is in flight) */
  useEffect(() => {
    if (status === "authenticated" && user && !signingRef.current) navigate(homeFor(user), { replace: true });
  }, [status, user, navigate]);

  return (
    <div className="site-page site-page--partner-login" data-testid="site-partner-login">
      <section className="account-layout">
        <div className="account-visual">
          <div className="media">
            <img src={c.visual.image} alt={c.visual.imageAlt || ""} onError={onPlaceholderError} />
          </div>
          <div className="account-visual-copy">
            <p className="eyebrow reveal" style={{ color: "rgba(248,246,242,.6)" }}>{c.visual.eyebrow}</p>
            <h1 className="display-xl reveal" style={{ marginTop: "var(--sp-3)" }}>{c.visual.title}</h1>
            <p className="body-lg reveal">{c.visual.bodyBefore}<strong>{c.visual.bodyStrong}</strong>{c.visual.bodyAfter}</p>
            <div className="who reveal">{c.visual.who.map((w) => <span key={w}>{w}</span>)}</div>
          </div>
        </div>

        <div className="account-panel">
          <div className="account-panel-inner">
            <div className="account-tabs" role="tablist">
              <button type="button" className={"account-tab" + (tab === "signin" ? " is-active" : "")} role="tab" aria-selected={tab === "signin"} data-pane="signin" id="tabSignin" onClick={() => showPane("signin")}>{c.tabs.signin}</button>
              <button type="button" className={"account-tab" + (tab === "apply" ? " is-active" : "")} role="tab" aria-selected={tab === "apply"} data-pane="apply" id="tabApply" onClick={() => showPane("apply")}>{c.tabs.apply}</button>
            </div>
            {tab === "signin"
              ? <SignInPane c={c} login={login} navigate={navigate} signingRef={signingRef} />
              : <ApplyPane c={c} />}
          </div>
        </div>
      </section>
    </div>
  );
}

function SignInPane({ c, login, navigate, signingRef }) {
  const s = c.signin;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const signIn = async (em, pw) => {
    if (busy) return;
    if (!em || !pw) { setErr(s.error); return; }
    setErr(""); setBusy(true); signingRef.current = true;
    try {
      const u = await login(em, pw);
      /* 2026-09-07 user: "remove that welcome-back animation, leave only the LHC
         animation" — signing in plays the brand beat over the mounting workspace */
      playBootSplash();
      navigate(homeFor(u), { replace: true });
    } catch (e) {
      signingRef.current = false;
      setErr(formatError(e && e.response && e.response.data && e.response.data.detail) || (e && e.message) || "Login failed");
      setBusy(false);
    }
  };

  return (
    <div className="account-pane is-active" id="paneSignin" role="tabpanel">
      <h2 className="display-md" style={{ marginBottom: "var(--sp-2)" }}>{s.title}</h2>
      <p className="body-md" style={{ marginBottom: "var(--sp-6)" }}>{s.sub}</p>
      <form id="signinForm" noValidate onSubmit={(e) => { e.preventDefault(); signIn(email.trim(), password); }}>
        <div className="form-row single">
          <div className="form-field"><label htmlFor="si-email">{s.email}</label><input id="si-email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" /></div>
        </div>
        <div className="form-row single">
          <div className="form-field"><label htmlFor="si-password">{s.password}</label><input id="si-password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" /></div>
        </div>
        <div className="form-meta">
          {/* as in the prototype, unchecked and inert — the token is stored by AuthContext either
              way; making it real would mean changing where the session is kept (AuthContext) */}
          <label className="form-check"><input type="checkbox" id="si-remember" /> {s.remember}</label>
          {/* the prototype points "Forgot password" at contact.html — resets go through the LHC team */}
          <SiteLink href={s.forgotHref} className="text-link" style={{ fontSize: 12 }} id="forgotLink">{s.forgot}</SiteLink>
        </div>
        <button type="submit" className="btn btn--solid btn--block" disabled={busy} data-testid="login-submit-button">{busy ? s.busy : s.button}</button>
        <p className={"form-error" + (err ? " is-visible" : "")} id="signinError" role="alert" data-testid="login-error">{err || s.error}</p>
      </form>
      <p className="form-foot">{s.footBefore}<SiteLink href="login.html#apply" id="switchToApply">{s.footLink}</SiteLink>{s.footAfter}</p>

      {/* the platform's evaluation accounts — one click signs in */}
      <div className="account-demo" data-testid="quick-login">
        <p className="eyebrow" style={{ marginBottom: "var(--sp-3)" }}>{c.demo.label}</p>
        <div className="account-types">
          {DEMO_ACCOUNTS.map((d) => (
            <button type="button" key={d.email} className="account-type" data-testid={`quick-login-${demoKey(d.email)}`} disabled={busy}
              onClick={() => { setEmail(d.email); setPassword(d.password); signIn(d.email, d.password); }}>
              <span className="t">{d.label}</span><span className="d">{d.tenant} · {d.email}</span>
            </button>
          ))}
        </div>
        {/* a plain status-note, NOT `is-placeholder`: the stylesheet prefixes that flag with
            "Placeholder — ", and these accounts really do sign you in — they are not a mock */}
        <p className="status-note">{c.demo.note}</p>
      </div>
    </div>
  );
}

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", company: "", website: "", country: "", city: "", showroom: "", markets: "", message: "", consent: false };

function ApplyPane({ c }) {
  const a = c.apply, f = a.fields;
  const [type, setType] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [err, setErr] = useState("");
  const [fieldErrs, setFieldErrs] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((v) => ({ ...v, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const selected = a.types.find((t) => t.id === type) || null;

  const submit = async (e) => {
    e.preventDefault();
    const missing = ["first_name", "last_name", "email", "company", "country"].some((k) => !String(form[k] || "").trim());
    if (missing || !selected || !form.consent) { setErr(a.error); return; }
    setErr(""); setFieldErrs({}); setBusy(true);
    try {
      await axios.post(`${API}/auth/signup`, { ...form, type: selected.id, showroom: selected.dealerFields ? form.showroom : "", markets: selected.dealerFields ? form.markets : "" });
      setDone(true);
    } catch (e2) {
      const d = e2 && e2.response && e2.response.data && e2.response.data.detail;
      setErr(formatError(d) || a.sendError);
      if (d && d.errors) setFieldErrs(d.errors);
    }
    setBusy(false);
  };

  if (done) {
    return (
      <div className="account-pane is-active" id="paneApply" role="tabpanel" data-testid="apply-done">
        <h2 className="display-md" style={{ marginBottom: "var(--sp-2)" }}>{a.title}</h2>
        <p className="form-confirm" style={{ marginTop: "var(--sp-5)" }}>{a.thanks}</p>
      </div>
    );
  }

  const bad = (k) => (fieldErrs[k] ? { borderColor: "#8B3A2E" } : undefined);
  return (
    <div className="account-pane is-active" id="paneApply" role="tabpanel">
      <h2 className="display-md" style={{ marginBottom: "var(--sp-2)" }}>{a.title}</h2>
      <p className="body-md" style={{ marginBottom: "var(--sp-6)" }}>{a.sub}</p>
      <form id="applyForm" noValidate onSubmit={submit} data-testid="apply-form">
        <div className="form-field" style={{ marginBottom: "var(--sp-3)" }}><label>{a.typeLabel}</label></div>
        <div className="account-types" id="accountTypes">
          {a.types.map((t) => (
            <button type="button" key={t.id} className={"account-type" + (type === t.id ? " is-active" : "")} data-type={t.id} aria-pressed={type === t.id} onClick={() => setType(t.id)}>
              <span className="t">{t.title}</span><span className="d">{t.desc}</span>
            </button>
          ))}
        </div>

        <div className="form-row">
          <div className="form-field"><label htmlFor="ap-first">{f.first}</label><input id="ap-first" type="text" required value={form.first_name} onChange={set("first_name")} style={bad("first_name")} /></div>
          <div className="form-field"><label htmlFor="ap-last">{f.last}</label><input id="ap-last" type="text" required value={form.last_name} onChange={set("last_name")} style={bad("last_name")} /></div>
        </div>
        <div className="form-row">
          <div className="form-field"><label htmlFor="ap-email">{f.email}</label><input id="ap-email" type="email" required value={form.email} onChange={set("email")} style={bad("email")} /></div>
          <div className="form-field"><label htmlFor="ap-phone">{f.phone}</label><input id="ap-phone" type="tel" value={form.phone} onChange={set("phone")} style={bad("phone")} /></div>
        </div>
        <div className="form-row">
          <div className="form-field"><label htmlFor="ap-company">{f.company}</label><input id="ap-company" type="text" required value={form.company} onChange={set("company")} style={bad("company")} /></div>
          <div className="form-field"><label htmlFor="ap-website">{f.website}</label><input id="ap-website" type="url" placeholder={f.websitePlaceholder} value={form.website} onChange={set("website")} style={bad("website")} /></div>
        </div>
        <div className="form-row">
          <div className="form-field"><label htmlFor="ap-country">{f.country}</label><input id="ap-country" type="text" required value={form.country} onChange={set("country")} style={bad("country")} /></div>
          <div className="form-field"><label htmlFor="ap-city">{f.city}</label><input id="ap-city" type="text" value={form.city} onChange={set("city")} /></div>
        </div>

        {/* the prototype keeps #dealerFields hidden until a dealer/agent card is picked */}
        {selected && selected.dealerFields ? (
          <div id="dealerFields">
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="ap-showroom">{f.showroom}</label>
                <select id="ap-showroom" value={form.showroom} onChange={set("showroom")}>
                  <option value="">{f.showroomSelect}</option>
                  {f.showroomOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="form-field"><label htmlFor="ap-markets">{f.markets}</label><input id="ap-markets" type="text" placeholder={f.marketsPlaceholder} value={form.markets} onChange={set("markets")} /></div>
            </div>
          </div>
        ) : null}

        <div className="form-row single">
          <div className="form-field"><label htmlFor="ap-message">{f.message}</label><textarea id="ap-message" placeholder={f.messagePlaceholder} value={form.message} onChange={set("message")} /></div>
        </div>
        <div className="form-meta">
          <label className="form-check"><input type="checkbox" id="ap-consent" required checked={form.consent} onChange={set("consent")} /> {a.consent}</label>
        </div>
        <button type="submit" className="btn btn--solid btn--block" disabled={busy} data-testid="apply-submit">{busy ? a.busy : a.button}</button>
        <p className={"form-error" + (err ? " is-visible" : "")} id="applyError" role="alert" data-testid="apply-error">{err || a.error}</p>
      </form>
      <p className="form-foot">{a.footBefore}<SiteLink href="login.html" id="switchToSignin">{a.footLink}</SiteLink>{a.footAfter}</p>
    </div>
  );
}
